import { oc } from "@orpc/contract";
import { z } from "zod";

/**
 * Scalar helpers
 */
const UUIDSchema = z.uuid();
const DecimalStringSchema = z.string().regex(/^-?\d+(\.\d+)?$/, {
  error: "Must be a numeric string",
});
const Decimal4dpSchema = z.string().regex(/^-?\d+(\.\d{1,4})?$/, {
  error: "Must be a number with at most 4 decimal places",
});

/**
 * VDM Status enum (matching backend model)
 */
export const VDMStatusSchema = z.enum([
  "created",
  "as400_confirmed_create",
  "pending_as400_edit",
  "as400_confirmed_edit",
  "error_creating",
  "error_editing",
]);

/**
 * Pagination schema
 */
export const VDMPaginationSchema = z.object({
  total: z.int().nonnegative(),
  limit: z.int().positive(),
  offset: z.int().nonnegative(),
});

/**
 * Supplier summary for VDM response
 */
const VDMSupplierSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  companyId: UUIDSchema.nullable().optional(),
});

/**
 * VDM schema for RPC responses
 */
export const VDMSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  vdmNumber: z.int().nullable().optional(),
  grossAmount: DecimalStringSchema,
  status: VDMStatusSchema,
  calculateTax: z.boolean(),
  shippingProvince: z.string().nullable().optional(),
  specialInstructions1: z.string().nullable().optional(),
  specialInstructions2: z.string().nullable().optional(),
  specialInstructions3: z.string().nullable().optional(),
  commentLine1: z.string().nullable().optional(),
  commentLine2: z.string().nullable().optional(),
  emailToAddress: z.string().nullable().optional(),
  emailCcAddress: z.string().nullable().optional(),
  emailBccAddress: z.string().nullable().optional(),
  companyCode: z.string(),
  divisionCode: z.string().nullable().optional(),
  branchCode: z.string().nullable().optional(),
  gstHstAmount: DecimalStringSchema.nullable().optional(),
  qstPstAmount: DecimalStringSchema.nullable().optional(),
  totalTaxAmount: DecimalStringSchema.nullable().optional(),
  totalAmount: DecimalStringSchema.nullable().optional(),
  error: z.string().nullable().optional(),
  supplierId: UUIDSchema.nullable(),
  supplierVendorNumber: z.string(),
  supplier: VDMSupplierSchema.nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  linkedSubmissionCount: z.int().nonnegative().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
});

/**
 * List VDMs input schema with filters
 */
export const VDMSortBySchema = z.enum([
  "createdAt",
  "grossAmount",
  "totalAmount",
  "gentlyVdmNumber",
  "vdmNumber",
]);

export const ListVDMsInputSchema = z.object({
  // Filter by status
  status: z.array(VDMStatusSchema).optional(),
  // Filter by supplier
  supplierId: UUIDSchema.optional(),
  // Filter by company code
  companyCode: z.string().optional(),
  // Filter by division code
  divisionCode: z.string().optional(),
  // Filter by gently VDM number
  gentlyVdmNumber: z.int().optional(),
  // Filter by external VDM number
  vdmNumber: z.int().optional(),
  // Text search (matches VDM number or supplier name)
  search: z.string().optional(),
  // Date filters
  createdAtFrom: z.string().optional(),
  createdAtTo: z.string().optional(),
  // Sorting
  sortBy: VDMSortBySchema.optional(),
  sortOrder: z.enum(["ASC", "DESC"]).optional(),
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDMs response schema
 */
export const ListVDMsResponseSchema = z.object({
  vdms: z.array(VDMSchema),
  pagination: VDMPaginationSchema,
});

/**
 * Get single VDM input schema
 */
export const GetVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Get VDM response schema
 */
export const GetVDMResponseSchema = VDMSchema;

/**
 * VDM Activity Type enum (matching backend model)
 */
export const VDMActivityTypeSchema = z.enum([
  "created",
  "sent_to_supplier",
  "supplier_replied",
  "reply_sent",
  "accepted",
  "rejected",
  "status_changed",
  "error_occurred",
  "resent",
  "manually_updated",
  "comment_added",
  "as400_responded",
  "submission_linked",
  "submission_unlinked",
]);

/**
 * User summary for activity response
 */
const ActivityUserSchema = z.object({
  id: UUIDSchema,
  name: z.string(),
});

/**
 * VDM Activity schema for RPC responses
 */
export const VDMActivitySchema = z.object({
  id: UUIDSchema,
  vdmId: UUIDSchema,
  activityType: VDMActivityTypeSchema,
  userId: UUIDSchema.nullable().optional(),
  user: ActivityUserSchema.nullable().optional(),
  inboundEmailId: UUIDSchema.nullable().optional(),
  eventAt: z.coerce.date(),
  description: z.string(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  createdAt: z.coerce.date(),
});

/**
 * List VDM Activities input schema
 */
export const ListVDMActivitiesInputSchema = z.object({
  vdmId: UUIDSchema,
  // Pagination
  limit: z.int().min(1).max(100).optional(),
  offset: z.int().min(0).optional(),
});

/**
 * List VDM Activities response schema
 */
export const ListVDMActivitiesResponseSchema = z.object({
  activities: z.array(VDMActivitySchema),
  pagination: VDMPaginationSchema,
});

/**
 * Add Comment input schema
 */
export const AddVDMCommentInputSchema = z.object({
  vdmId: UUIDSchema,
  comment: z.string().min(1).max(2000),
});

/**
 * Add Comment response schema
 */
export const AddVDMCommentResponseSchema = VDMActivitySchema;

/**
 * Create VDM input schema
 */
export const CreateVDMInputSchema = z.object({
  companyCode: z.string().min(1),
  divisionCode: z.string(),
  branchCode: z.string(),
  calculateTax: z.boolean(),
  shippingProvince: z.string().min(1),
  grossAmount: Decimal4dpSchema,
  specialInstructions1: z.string().max(30).optional(),
  specialInstructions2: z.string().max(30).optional(),
  specialInstructions3: z.string().max(30).optional(),
  commentLine1: z.string().optional(),
  commentLine2: z.string().optional(),
  emailToAddress: z.string().email().optional().or(z.literal("")),
  emailCcAddress: z.string().max(70).optional().or(z.literal("")),
  emailBccAddress: z.string().max(70).optional().or(z.literal("")),
  supplierVendorNumber: z.string(),
  transactionIds: z.array(UUIDSchema).optional(),
});

/**
 * Create VDM response schema
 */
export const CreateVDMResponseSchema = VDMSchema;

/**
 * Batch VDM creation — create multiple VDMs at once (used by auto-VDM after batch-and-send).
 */
export const CreateBatchVDMsInputSchema = z.object({
  vdms: z.array(CreateVDMInputSchema).min(1).max(100),
});

const BatchVDMCreatedSchema = z.object({
  vdmId: UUIDSchema,
  gentlyVdmNumber: z.int(),
  supplierVendorNumber: z.string(),
  companyCode: z.string(),
  divisionCode: z.string(),
});

const BatchVDMFailedSchema = z.object({
  index: z.int(),
  reason: z.string(),
});

export const CreateBatchVDMsResponseSchema = z.object({
  created: z.array(BatchVDMCreatedSchema),
  failed: z.array(BatchVDMFailedSchema),
});

/**
 * Update VDM input schema (partial update, only editable fields)
 */
export const UpdateVDMInputSchema = z.object({
  id: UUIDSchema,
  supplierId: UUIDSchema.optional(),
  supplierVendorNumber: z.string().min(1).optional(),
  companyCode: z.string().min(1).optional(),
  divisionCode: z.string().min(1).optional(),
  branchCode: z.string().min(1).optional(),
  grossAmount: Decimal4dpSchema.optional(),
  calculateTax: z.boolean().optional(),
  shippingProvince: z.string().min(1).optional(),
  emailToAddress: z.string().email().optional().or(z.literal("")),
  emailCcAddress: z.string().max(70).optional().or(z.literal("")),
  emailBccAddress: z.string().max(70).optional().or(z.literal("")),
  specialInstructions1: z.string().max(30).optional(),
  specialInstructions2: z.string().max(30).optional(),
  specialInstructions3: z.string().max(30).optional(),
  commentLine1: z.string().optional(),
  commentLine2: z.string().optional(),
});

/**
 * Update VDM response schema
 */
export const UpdateVDMResponseSchema = VDMSchema;

/**
 * Send VDM input schema
 */
export const SendVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Send VDM response schema
 */
export const SendVDMResponseSchema = VDMSchema;

/**
 * Refresh AS400 input schema
 */
export const RefreshAS400InputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Refresh AS400 response schema
 */
export const RefreshAS400ResponseSchema = z.discriminatedUnion("found", [
  z.object({ found: z.literal(true), vdm: VDMSchema, message: z.string() }),
  z.object({ found: z.literal(false), message: z.string() }),
]);

/**
 * Refresh All AS400 response schema — bulk refresh for all pending VDMs
 */
export const RefreshAllAS400ResponseSchema = z.object({
  updatedCount: z.int().nonnegative(),
  checkedCount: z.int().nonnegative(),
  message: z.string(),
});

/**
 * Delete VDM input schema
 */
export const DeleteVDMInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Delete VDM response schema
 */
export const DeleteVDMResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * VDM supplier company (from co_companies with S&D module enabled)
 */
export const VDMSupplierCompanySchema = z.object({
  id: UUIDSchema,
  name: z.string(),
  vendorNumber: z.string(),
  vdmCcEmail: z.string().nullable(),
});

export const ListVDMCompaniesResponseSchema = z.object({
  companies: z.array(VDMSupplierCompanySchema),
});

/**
 * Company hierarchy from sd_company_locations table
 */
export const CompanyHierarchyBranchSchema = z.object({
  code: z.string(),
  description: z.string(),
});

export const CompanyHierarchyDivisionSchema = z.object({
  code: z.string(),
  name: z.string().nullable().optional(),
  branches: z.array(CompanyHierarchyBranchSchema),
});

export const CompanyHierarchyCompanySchema = z.object({
  code: z.string(),
  name: z.string().nullable().optional(),
  divisions: z.array(CompanyHierarchyDivisionSchema),
});

export const GetCompanyHierarchyResponseSchema = z.object({
  companies: z.array(CompanyHierarchyCompanySchema),
});

/**
 * Linked 844 submissions for a VDM
 */
export const GetVDMLinkedSubmissionsInputSchema = z.object({
  vdmId: UUIDSchema,
});

const LinkedSubmissionSummarySchema = z.object({
  id: UUIDSchema,
  submissionNumber: z.int(),
  companyCode: z.string(),
  divisionCode: z.string(),
  sellingLocationCode: z.string(),
  deliveryMethod: z.string(),
  totalClaimedAmount: DecimalStringSchema,
  status: z.string(),
  externalReference: z.string().nullable().optional(),
  lineCount: z.int().nonnegative().optional(),
  sentAt: z.coerce.date().nullable().optional(),
  company: z.object({ id: UUIDSchema, name: z.string() }).nullable().optional(),
});

export const GetVDMLinkedSubmissionsResponseSchema = z.object({
  submissions: z.array(LinkedSubmissionSummarySchema),
});

export const LinkSubmissionToVDMFromVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  submissionId: UUIDSchema,
});

export const LinkSubmissionToVDMFromVDMResponseSchema = z.object({
  success: z.boolean(),
});

export const UnlinkSubmissionFromVDMFromVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  submissionId: UUIDSchema,
});

export const UnlinkSubmissionFromVDMFromVDMResponseSchema = z.object({
  success: z.boolean(),
});

/**
 * Per-line linking: attach a VDM to specific 844 transactions (sd_e844d ids).
 * Used by the 844 submission detail modal to link/unlink on a per-line basis.
 * The backend validates that every selected transaction is in the VDM's tax
 * province before linking.
 */
export const LinkTransactionsToVDMInputSchema = z.object({
  vdmId: UUIDSchema,
  transactionIds: z.array(UUIDSchema).min(1),
});

export const LinkTransactionsToVDMResponseSchema = z.object({
  linkedCount: z.int(),
});

export const UnlinkTransactionsFromVDMInputSchema = z.object({
  transactionIds: z.array(UUIDSchema).min(1),
  // When provided, only unlink rows currently pointing at this VDM — guards
  // against a stale modal unlinking a link another user just changed.
  vdmId: UUIDSchema.optional(),
});

export const UnlinkTransactionsFromVDMResponseSchema = z.object({
  unlinkedCount: z.int(),
});

/**
 * VDM RPC Contracts
 */
export const vdmContracts = {
  listCompanies: oc
    .output(ListVDMCompaniesResponseSchema)
    .route({ method: "GET" }),
  getCompanyHierarchy: oc
    .output(GetCompanyHierarchyResponseSchema)
    .route({ method: "GET" }),
  list: oc.input(ListVDMsInputSchema).output(ListVDMsResponseSchema).route({
    method: "GET",
  }),
  get: oc.input(GetVDMInputSchema).output(GetVDMResponseSchema).route({
    method: "GET",
  }),
  create: oc.input(CreateVDMInputSchema).output(CreateVDMResponseSchema).route({
    method: "POST",
  }),
  update: oc.input(UpdateVDMInputSchema).output(UpdateVDMResponseSchema).route({
    method: "POST",
  }),
  send: oc.input(SendVDMInputSchema).output(SendVDMResponseSchema).route({
    method: "POST",
  }),
  retryCreation: oc
    .input(UpdateVDMInputSchema)
    .output(SendVDMResponseSchema)
    .route({
      method: "POST",
    }),
  delete: oc.input(DeleteVDMInputSchema).output(DeleteVDMResponseSchema).route({
    method: "POST",
  }),
  refreshAS400: oc
    .input(RefreshAS400InputSchema)
    .output(RefreshAS400ResponseSchema)
    .route({
      method: "POST",
    }),
  refreshAllAS400: oc.output(RefreshAllAS400ResponseSchema).route({
    method: "POST",
  }),
  listActivities: oc
    .input(ListVDMActivitiesInputSchema)
    .output(ListVDMActivitiesResponseSchema)
    .route({
      method: "GET",
    }),
  addComment: oc
    .input(AddVDMCommentInputSchema)
    .output(AddVDMCommentResponseSchema)
    .route({
      method: "POST",
    }),
  createBatch: oc
    .input(CreateBatchVDMsInputSchema)
    .output(CreateBatchVDMsResponseSchema)
    .route({
      method: "POST",
    }),
  getLinkedSubmissions: oc
    .input(GetVDMLinkedSubmissionsInputSchema)
    .output(GetVDMLinkedSubmissionsResponseSchema)
    .route({
      method: "GET",
    }),
  linkSubmission: oc
    .input(LinkSubmissionToVDMFromVDMInputSchema)
    .output(LinkSubmissionToVDMFromVDMResponseSchema)
    .route({
      method: "POST",
    }),
  unlinkSubmission: oc
    .input(UnlinkSubmissionFromVDMFromVDMInputSchema)
    .output(UnlinkSubmissionFromVDMFromVDMResponseSchema)
    .route({
      method: "POST",
    }),
  linkTransactions: oc
    .input(LinkTransactionsToVDMInputSchema)
    .output(LinkTransactionsToVDMResponseSchema)
    .route({
      method: "POST",
    }),
  unlinkTransactions: oc
    .input(UnlinkTransactionsFromVDMInputSchema)
    .output(UnlinkTransactionsFromVDMResponseSchema)
    .route({
      method: "POST",
    }),
} as const;

/**
 * Company / Division / Branch hierarchy for VDM creation.
 * A branch belongs to exactly one division; a division belongs to exactly one company.
 */
export interface VDMBranch {
  code: string;
}

export interface VDMDivision {
  code: string;
  branches: VDMBranch[];
}

export interface VDMCompany {
  code: string;
  divisions: VDMDivision[];
}

/** @deprecated Use the `getCompanyHierarchy` RPC endpoint instead, which loads from `sd_company_locations` table. */
export const VDM_COMPANY_HIERARCHY: VDMCompany[] = [
  {
    code: "W",
    divisions: [
      { code: "+", branches: [] },
      { code: "A", branches: [{ code: "1" }, { code: "2" }] },
      { code: "B", branches: [{ code: "3" }, { code: "4" }] },
      { code: "C", branches: [{ code: "5" }, { code: "6" }] },
      { code: "D", branches: [{ code: "7" }, { code: "10" }] },
      { code: "E", branches: [{ code: "11" }, { code: "12" }] },
      { code: "F", branches: [{ code: "13" }, { code: "14" }] },
      { code: "G", branches: [{ code: "15" }, { code: "16" }] },
      { code: "H", branches: [{ code: "17" }, { code: "18" }] },
      { code: "J", branches: [] },
      { code: "R", branches: [] },
      { code: "T", branches: [{ code: "8" }, { code: "9" }] },
    ],
  },
  {
    code: "C",
    divisions: [{ code: "J", branches: [{ code: "K" }] }],
  },
  {
    code: "Q",
    divisions: [
      { code: "E", branches: [] },
      { code: "O", branches: [] },
    ],
  },
  {
    code: "R",
    divisions: [
      { code: "A", branches: [] },
      { code: "H", branches: [] },
    ],
  },
];

// Type exports
export type VDMStatus = z.infer<typeof VDMStatusSchema>;
export type VDMSortBy = z.infer<typeof VDMSortBySchema>;
export type VDM = z.infer<typeof VDMSchema>;
export type ListVDMsInput = z.infer<typeof ListVDMsInputSchema>;
export type ListVDMsResponse = z.infer<typeof ListVDMsResponseSchema>;
export type GetVDMInput = z.infer<typeof GetVDMInputSchema>;
export type GetVDMResponse = z.infer<typeof GetVDMResponseSchema>;
export type VDMActivityType = z.infer<typeof VDMActivityTypeSchema>;
export type VDMActivity = z.infer<typeof VDMActivitySchema>;
export type ListVDMActivitiesInput = z.infer<
  typeof ListVDMActivitiesInputSchema
>;
export type ListVDMActivitiesResponse = z.infer<
  typeof ListVDMActivitiesResponseSchema
>;
export type AddVDMCommentInput = z.infer<typeof AddVDMCommentInputSchema>;
export type AddVDMCommentResponse = z.infer<typeof AddVDMCommentResponseSchema>;
export type CreateVDMInput = z.infer<typeof CreateVDMInputSchema>;
export type CreateVDMResponse = z.infer<typeof CreateVDMResponseSchema>;
export type CreateBatchVDMsInput = z.infer<typeof CreateBatchVDMsInputSchema>;
export type CreateBatchVDMsResponse = z.infer<
  typeof CreateBatchVDMsResponseSchema
>;
export type UpdateVDMInput = z.infer<typeof UpdateVDMInputSchema>;
export type UpdateVDMResponse = z.infer<typeof UpdateVDMResponseSchema>;
export type SendVDMInput = z.infer<typeof SendVDMInputSchema>;
export type SendVDMResponse = z.infer<typeof SendVDMResponseSchema>;
export type RefreshAS400Input = z.infer<typeof RefreshAS400InputSchema>;
export type RefreshAS400Response = z.infer<typeof RefreshAS400ResponseSchema>;
export type RefreshAllAS400Response = z.infer<
  typeof RefreshAllAS400ResponseSchema
>;
export type DeleteVDMInput = z.infer<typeof DeleteVDMInputSchema>;
export type DeleteVDMResponse = z.infer<typeof DeleteVDMResponseSchema>;
export type VDMSupplierCompany = z.infer<typeof VDMSupplierCompanySchema>;
export type ListVDMCompaniesResponse = z.infer<
  typeof ListVDMCompaniesResponseSchema
>;
export type CompanyHierarchyBranch = z.infer<
  typeof CompanyHierarchyBranchSchema
>;
export type CompanyHierarchyDivision = z.infer<
  typeof CompanyHierarchyDivisionSchema
>;
export type CompanyHierarchyCompany = z.infer<
  typeof CompanyHierarchyCompanySchema
>;
export type GetCompanyHierarchyResponse = z.infer<
  typeof GetCompanyHierarchyResponseSchema
>;
